import React, { useState, useMemo } from 'react';
import { Table, TABLE_LAYOUT_OPTIONS, Link } from 'cdk-radial';

import Loading from '../../common/Loading';
import { PrivacyProtectionOnApiIndicator } from './PrivacyProtectionIndicator';

const getConnectionStatus = status => {
  switch (status) {
    case 'active':
    case 'enabled':
      return 'Active';
    case 'inactive':
    case 'disabled':
      return 'Inactive';
    case 'pending':
      return 'Pending';
    default:
      return 'N/A';
  }
};

const PrivacyProtectionHeader = () => (
  <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
    <span>CPP Enabled</span>
    <PrivacyProtectionOnApiIndicator />
  </div>
);

const customCellRenderer = gridParams => {
  const { colDef, data } = gridParams;
  const { field } = colDef;
  return (
    <div className="cell-text-wrap" title={data[field]}>
      {data[field]}
    </div>
  );
};

export const ApiDetailsTable = ({
  apiData,
  listingVersionDetails,
  connectionData,
  accessGrantData,
  fetchingConnections,
  fetchingAccessGrants,
  setFieldsDialogApi,
  setFieldsDialogOpen
}) => {
  const [gridApi, setGridApi] = useState(null);

  const columnDefs = [
    {
      headerName: 'Name',
      field: 'displayName',
      cellRendererFramework: customCellRenderer,
      wrapText: true,
      minWidth: 280,
      autoHeight: true,
      hide: false
    },
    {
      headerName: 'Status',
      field: 'status',
      minWidth: 80,
      hide: false
    },
    {
      headerName: 'Listing Version',
      field: 'listingVersion',
      minWidth: 120,
      hide: false
    },
    {
      headerName: 'CPP Enabled',
      headerComponentFramework: PrivacyProtectionHeader,
      field: 'privacyProtection',
      minWidth: 175,
      cellRendererFramework: params => {
        return params.value ? 'Yes' : 'No';
      }
    },
    {
      headerName: 'API Fields',
      field: 'fields',
      minWidth: 50,
      cellRendererFramework: params =>
        params.value?.hasFields ? (
          <Link
            href="#"
            onClick={e => {
              // eslint-disable-next-line no-unused-expressions
              e?.preventDefault();
              setFieldsDialogApi(params.value.item);
              setFieldsDialogOpen(true);
            }}
            text="View"
          />
        ) : (
          'N/A'
        )
    }
  ];

  const retrieveListingVersion = apiName => {
    let listingVersionNumber;

    if (listingVersionDetails) {
      for (const [listingVersion, listingApiList] of Object.entries(
        listingVersionDetails
      )) {
        const isApiExistsInListingVersion =
          Array.isArray(listingApiList) &&
          listingApiList.find(f => f.apiName === apiName);
        if (isApiExistsInListingVersion && !listingVersionNumber) {
          listingVersionNumber = listingVersion;
        }
      }
    }

    return listingVersionNumber;
  };

  const rowData = useMemo(() => {
    return Array.isArray(apiData)
      ? apiData.map(item => {
          const apiId = item.id;
          const isAsync = item.apiType === 'async-api';

          const status = getConnectionStatus(
            isAsync
              ? accessGrantData[apiId]?.status
              : connectionData[apiId]?.status
          );

          const hasFields = !!(
            item?.apiFields?.length || item?.apiFieldsTestEnv?.length
          );

          return {
            ...item,
            status,
            listingVersion: retrieveListingVersion(item.displayName) || 'N/A',
            fields: { hasFields, item }
          };
        })
      : [];
  }, [apiData, accessGrantData, connectionData]);

  if (!apiData?.length) return <div>N/A</div>;
  if (fetchingAccessGrants || fetchingConnections)
    return (
      <div data-testId="loader">
        <Loading circleClass="subscription_loader" />
      </div>
    );

  const handleGridReady = gridParams => {
    setGridApi(gridParams.api);
  };

  return (
    <div className="subscription-data-api-table">
      <Table
        columns={columnDefs}
        rowHeight={40}
        data={rowData}
        isSearchable={false}
        isDownloadable={false}
        isPrintable={false}
        resizableHeaders={true}
        showNoRowsOverlay={false}
        showFooter={false}
        hideTableTitle={true}
        hideBorder={true}
        hasPagination={false}
        onGridReady={handleGridReady}
        dataTestId="subscription-data-api-table"
        tableLayout={TABLE_LAYOUT_OPTIONS.AUTO_HEIGHT}
        id="subscription-data-api-table"
        suppressRowClickSelection={true}
      />
    </div>
  );
};
